/**
 * AdminCuentasDestino — Gestión de cuentas destino de pago.
 *
 * Ruta: /admin/cuentas-destino (solo admin/superadmin)
 *
 * Permite crear, editar, desactivar y eliminar las cuentas destino que el
 * wizard de booking ofrece al elegir Nequi/Wompi/Transferencia/Bold/Tarjeta.
 * Si una cuenta tiene reservas asociadas, el delete la desactiva en lugar de
 * borrarla (preserva trazabilidad histórica).
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, TextField, MenuItem, IconButton, Chip, Switch,
  FormControl, InputLabel, Select, CircularProgress, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import useToast from './common/useToast';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getEmpresaId() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u.company_id || u.empresa_id || '1';
  } catch { return '1'; }
}
function getAuthHeaders() {
  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('user_id');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(userId ? { 'X-User-Id': userId } : {}),
  };
}

const TIPOS = [
  { value: 'nequi',       label: 'Nequi',        icon: '📲', color: '#a855f7' },
  { value: 'wompi',       label: 'Wompi',        icon: '📲', color: '#0891b2' },
  { value: 'bancolombia', label: 'Bancolombia',  icon: '🏦', color: '#fbbf24' },
  { value: 'banco_bogota', label: 'Banco de Bogotá', icon: '🏦', color: '#1e40af' },
  { value: 'bold',        label: 'Bold',         icon: '💳', color: '#10b981' },
  { value: 'datafono',    label: 'Datáfono',     icon: '💳', color: '#6366f1' },
];

const emptyForm = {
  nombre: '', tipo: 'nequi', titular: '', notas: '',
  activo: true, orden: '0',
};

export default function AdminCuentasDestino() {
  const empresaId = getEmpresaId();
  const { toast, ToastContainer } = useToast();

  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTipo, setFilterTipo] = useState('');
  const [filterEstado, setFilterEstado] = useState('todas');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${API_BASE}/turismo/cuentas-destino-pago?empresa_id=${empresaId}&solo_activos=false`,
        { headers: getAuthHeaders() }
      );
      setCuentas(r.ok ? await r.json() : []);
    } catch (e) { toast.error('Error cargando cuentas'); }
    setLoading(false);
  }, [empresaId]);  // eslint-disable-line

  useEffect(() => { cargar(); }, [cargar]);

  const filteredCuentas = useMemo(() => {
    return cuentas.filter(c => {
      if (filterTipo && c.tipo !== filterTipo) return false;
      if (filterEstado === 'activas' && !c.activo) return false;
      if (filterEstado === 'inactivas' && c.activo) return false;
      return true;
    });
  }, [cuentas, filterTipo, filterEstado]);

  const countsByTipo = useMemo(() => {
    return cuentas.reduce((acc, c) => {
      acc[c.tipo] = (acc[c.tipo] || 0) + 1;
      return acc;
    }, {});
  }, [cuentas]);

  const handleNew = () => {
    setEditItem(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const handleEdit = (c) => {
    setEditItem(c);
    setForm({
      nombre: c.nombre, tipo: c.tipo,
      titular: c.titular || '', notas: c.notas || '',
      activo: c.activo, orden: String(c.orden ?? 0),
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) return toast.error('Falta el nombre');
    if (!form.tipo) return toast.error('Selecciona un tipo');
    setSaving(true);
    try {
      const body = {
        nombre: form.nombre.trim(),
        tipo: form.tipo,
        titular: form.titular?.trim() || null,
        notas: form.notas?.trim() || null,
        activo: form.activo,
        orden: parseInt(form.orden) || 0,
      };
      if (!editItem) body.empresa_id = empresaId;
      const url = editItem
        ? `${API_BASE}/turismo/cuentas-destino-pago/${editItem.id}`
        : `${API_BASE}/turismo/cuentas-destino-pago`;
      const res = await fetch(url, {
        method: editItem ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Error');
      }
      toast.success(editItem ? 'Cuenta actualizada' : 'Cuenta creada');
      setDialogOpen(false);
      cargar();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const handleToggleActivo = async (c, nuevo) => {
    setCuentas(cs => cs.map(x => x.id === c.id ? { ...x, activo: nuevo } : x));
    try {
      const res = await fetch(`${API_BASE}/turismo/cuentas-destino-pago/${c.id}`, {
        method: 'PUT', headers: getAuthHeaders(),
        body: JSON.stringify({ activo: nuevo }),
      });
      if (!res.ok) throw new Error('Error');
      toast.success(nuevo ? 'Cuenta activada' : 'Cuenta desactivada');
    } catch (e) {
      setCuentas(cs => cs.map(x => x.id === c.id ? { ...x, activo: !nuevo } : x));
      toast.error('No se pudo actualizar');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`${API_BASE}/turismo/cuentas-destino-pago/${deleteTarget.id}`, {
        method: 'DELETE', headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo eliminar');
      toast.success(data.detail || 'Eliminada');
      setDeleteTarget(null);
      cargar();
    } catch (e) { toast.error(e.message); }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <ToastContainer />

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1.5 }}>
        <AccountBalanceIcon sx={{ fontSize: 32, color: '#0d9488' }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Cuentas destino de pago</Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>
            Estas son las cuentas que el wizard ofrece al elegir Nequi / Wompi / Transferencia / Bold / Tarjeta.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleNew}
          sx={{ textTransform: 'none', fontWeight: 700,
                background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
          Nueva cuenta
        </Button>
      </Box>

      {/* KPI strip por tipo */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {TIPOS.map(t => (
          <Box key={t.value} sx={{
            px: 1.5, py: 0.75, borderRadius: 2, bgcolor: '#fff',
            border: `1px solid ${t.color}30`,
            display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
            ...(filterTipo === t.value && { borderColor: t.color, bgcolor: `${t.color}10` }),
          }} onClick={() => setFilterTipo(filterTipo === t.value ? '' : t.value)}>
            <Typography sx={{ fontSize: 16 }}>{t.icon}</Typography>
            <Typography sx={{ fontSize: 12, color: '#475569' }}>{t.label}</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: t.color }}>
              {countsByTipo[t.value] || 0}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Filtros */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Tipo</InputLabel>
          <Select label="Tipo" value={filterTipo} onChange={e => setFilterTipo(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            {TIPOS.map(t => <MenuItem key={t.value} value={t.value}>{t.icon} {t.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Estado</InputLabel>
          <Select label="Estado" value={filterEstado} onChange={e => setFilterEstado(e.target.value)}>
            <MenuItem value="todas">Todas</MenuItem>
            <MenuItem value="activas">Activas</MenuItem>
            <MenuItem value="inactivas">Inactivas</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Tabla */}
      {filteredCuentas.length === 0 ? (
        <Box sx={{ p: 6, textAlign: 'center', bgcolor: '#fff', borderRadius: 2, border: '1px dashed #cbd5e1' }}>
          <AccountBalanceIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
          <Typography sx={{ fontSize: 14, color: '#64748b' }}>
            {cuentas.length === 0 ? 'No hay cuentas configuradas' : 'No hay cuentas con esos filtros'}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <Box component="thead" sx={{ bgcolor: '#f8fafc' }}>
                <Box component="tr">
                  {['Tipo', 'Nombre', 'Titular', 'Notas', 'Orden', 'Activa', ''].map(h => (
                    <Box key={h} component="th"
                      sx={{ p: 1.25, textAlign: 'left', fontSize: 11, fontWeight: 700,
                            color: '#475569', textTransform: 'uppercase', letterSpacing: 0.3,
                            borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                      {h}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {filteredCuentas.map(c => {
                  const tipoCfg = TIPOS.find(t => t.value === c.tipo) || { icon: '💰', label: c.tipo, color: '#64748b' };
                  return (
                    <Box component="tr" key={c.id}
                      sx={{ borderBottom: '1px solid #f1f5f9',
                            '&:hover': { bgcolor: '#fafbfc' } }}>
                      <Box component="td" sx={{ p: 1.25 }}>
                        <Chip label={<span>{tipoCfg.icon} {tipoCfg.label}</span>} size="small"
                          sx={{ bgcolor: `${tipoCfg.color}15`, color: tipoCfg.color, fontWeight: 700, fontSize: 11, height: 22 }} />
                      </Box>
                      <Box component="td" sx={{ p: 1.25, fontWeight: 700, color: '#0f172a' }}>
                        {c.nombre}
                      </Box>
                      <Box component="td" sx={{ p: 1.25, color: '#475569' }}>
                        {c.titular || '—'}
                      </Box>
                      <Box component="td" sx={{ p: 1.25, color: '#64748b', fontSize: 11, maxWidth: 240 }}>
                        {c.notas || '—'}
                      </Box>
                      <Box component="td" sx={{ p: 1.25, textAlign: 'center', color: '#64748b' }}>
                        {c.orden}
                      </Box>
                      <Box component="td" sx={{ p: 1.25 }}>
                        <Switch size="small" checked={c.activo}
                          onChange={(e) => handleToggleActivo(c, e.target.checked)} />
                      </Box>
                      <Box component="td" sx={{ p: 1.25, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => handleEdit(c)} sx={{ color: '#0d9488' }}>
                            <EditIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar (o desactivar si tiene reservas)">
                          <IconButton size="small" onClick={() => setDeleteTarget(c)} sx={{ color: '#dc2626' }}>
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Dialog crear/editar */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)', color: '#fff',
          display: 'flex', alignItems: 'center', gap: 1, py: 1.5,
        }}>
          <AccountBalanceIcon />
          <Typography sx={{ fontSize: 16, fontWeight: 800, flex: 1 }}>
            {editItem ? 'Editar cuenta' : 'Nueva cuenta destino'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Tipo *</InputLabel>
              <Select label="Tipo *" value={form.tipo}
                onChange={e => setForm({ ...form, tipo: e.target.value })}>
                {TIPOS.map(t => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.icon} {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Nombre visible *" size="small" fullWidth value={form.nombre}
              onChange={e => setForm({ ...form, nombre: e.target.value })}
              placeholder="Ej: Nequi Entretur, Bancolombia Julián, etc." />
            <TextField label="Titular" size="small" fullWidth value={form.titular}
              onChange={e => setForm({ ...form, titular: e.target.value })}
              placeholder="A nombre de quién está la cuenta" />
            <TextField label="Notas internas" size="small" fullWidth multiline rows={2}
              value={form.notas} onChange={e => setForm({ ...form, notas: e.target.value })}
              placeholder="Número, comentarios, restricciones, etc. (opcional)" />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, alignItems: 'center' }}>
              <TextField label="Orden" size="small" type="number" value={form.orden}
                onChange={e => setForm({ ...form, orden: e.target.value })}
                helperText="Posición en el selector (menor = más arriba)" />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Switch checked={form.activo} onChange={e => setForm({ ...form, activo: e.target.checked })} />
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  {form.activo ? 'Activa' : 'Inactiva'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}
            sx={{ textTransform: 'none', fontWeight: 700,
                  background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
            {saving ? 'Guardando...' : (editItem ? 'Guardar cambios' : 'Crear cuenta')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm delete */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>¿Eliminar cuenta?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            ¿Eliminar la cuenta <strong>{deleteTarget?.nombre}</strong>?<br/>
            <span style={{ fontSize: 12, color: '#64748b' }}>
              Si tiene reservas asociadas, no se borra: solo se desactiva para preservar el histórico.
            </span>
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button onClick={handleDelete} variant="contained" color="error" sx={{ textTransform: 'none', fontWeight: 700 }}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
